import { type ResultOf } from '@graphql-typed-document-node/core'
import { type TFunction } from 'i18next'

import { graphql } from '~/gql'

import { compact, exists } from '../generals'

const brandAccountOpHasReadiedSurveyGroupsBrandAccountFragment = graphql(`
  fragment BrandAccountOp_HasReadiedSurveyGroups_BrandAccount on BrandAccount {
    id
    surveyGroups {
      id
      surveys {
        id
      }
      brands {
        id
      }
      questions {
        id
      }
    }
  }
`)

type BrandAccountFragment = Required<
  Pick<
    ResultOf<typeof brandAccountOpHasReadiedSurveyGroupsBrandAccountFragment>,
    'surveyGroups'
  >
> &
  ResultOf<typeof brandAccountOpHasReadiedSurveyGroupsBrandAccountFragment>
export class BrandAccountOp {
  static hasReadiedSurveyGroups = (brandAccount: BrandAccountFragment) =>
    (brandAccount.surveyGroups ?? []).some(
      (surveyGroup) =>
        0 < (surveyGroup.surveys ?? []).length &&
        (0 < (surveyGroup.brands ?? []).length ||
          0 < (surveyGroup.questions ?? []).length),
    )
}

const brandGroupOpIsMappedBrandGroupFragment = graphql(`
  fragment BrandGroupOp_IsMapped_BrandGroup on BrandGroup {
    mappedAt
  }
`)

const brandGroupOpHasBrandsBrandGroupFragment = graphql(`
  fragment BrandGroupOp_HasBrands_BrandGroup on BrandGroup {
    brands {
      id
    }
  }
`)

export class BrandGroupOp {
  static isMapped = (
    brandGroup: ResultOf<typeof brandGroupOpIsMappedBrandGroupFragment>,
  ) => exists(brandGroup.mappedAt)

  static hasBrands = (
    brandGroup: ResultOf<typeof brandGroupOpHasBrandsBrandGroupFragment>,
    numberOfBrands = 1,
  ) => numberOfBrands <= (brandGroup.brands ?? []).length
}

const surveyOpGetSurveyStartDateSurveyFragment = graphql(`
  fragment SurveyOp_GetSurveyStartDate_Survey on Survey {
    metadata {
      surveyStartDate {
        year
        month
        day
      }
    }
  }
`)

const surveyOpCompareByStartDateSurveyFragment = graphql(`
  fragment SurveyOp_CompareByStartDate_Survey on Survey {
    ...SurveyOp_GetSurveyStartDate_Survey
  }
`)

const surveyOpHasMappedBrandGroupsWithBrandsSurveyFragment = graphql(`
  fragment SurveyOp_HasMappedBrandGroupsWithBrands_Survey on Survey {
    brandGroups {
      brands {
        id
      }
      mappedAt
    }
  }
`)

export class SurveyOp {
  static getSurveyStartDate = (
    survey: ResultOf<typeof surveyOpGetSurveyStartDateSurveyFragment>,
  ) => {
    const surveyStartDate = survey.metadata?.surveyStartDate

    return exists(surveyStartDate)
      ? new Date(
          surveyStartDate.year,
          surveyStartDate.month - 1,
          surveyStartDate.day,
        )
      : null
  }

  static compareByStartDate = <
    TSurvey extends ResultOf<typeof surveyOpCompareByStartDateSurveyFragment>,
  >(
    a: TSurvey,
    b: TSurvey,
  ) => {
    const aTime = this.getSurveyStartDate(a)?.getTime() ?? 0
    const bTime = this.getSurveyStartDate(b)?.getTime() ?? 0

    return aTime - bTime
  }

  static hasMappedBrandGroupsWithBrands = (
    survey: ResultOf<
      typeof surveyOpHasMappedBrandGroupsWithBrandsSurveyFragment
    >,
    numberOfBrands = 1,
  ) =>
    (survey.brandGroups ?? []).some(
      (brandGroup) =>
        exists(brandGroup) &&
        BrandGroupOp.isMapped(brandGroup) &&
        BrandGroupOp.hasBrands(brandGroup, numberOfBrands),
    )
}

const questionOpToDisplayIdQuestionFragment = graphql(`
  fragment QuestionOp_ToDisplayId_Question on Question {
    question_id
  }
`)

const questionOpToTextQuestionFragment = graphql(`
  fragment QuestionOp_ToText_Question on Question {
    ...QuestionOp_ToDisplayId_Question
    question_text
  }
`)

const matrixQuestionPrefix = '__9segs_MT_'

export class QuestionOp {
  static toDisplayId = (
    question: ResultOf<typeof questionOpToDisplayIdQuestionFragment>,
  ) =>
    question.question_id.startsWith(matrixQuestionPrefix)
      ? question.question_id.slice(matrixQuestionPrefix.length)
      : question.question_id

  static toText = (
    question: ResultOf<typeof questionOpToTextQuestionFragment>,
  ) => {
    const id = this.toDisplayId(question)

    return `[${id}] ${question.question_text}`
  }
}

const customerAttributeOpGetFromGroupsCustomerAttributeGroupFragment = graphql(`
  fragment CustomerAttributeOp_GetFromGroups_CustomerAttributeGroup on CustomerAttributeGroup {
    customerAttributes {
      id
    }
  }
`)

const customerAttributeOpGetNameFromGroupsCustomerAttributeGroupFragment =
  graphql(`
    fragment CustomerAttributeOp_GetNameFromGroups_CustomerAttributeGroup on CustomerAttributeGroup {
      ...CustomerAttributeOp_GetFromGroups_CustomerAttributeGroup
      name
      customerAttributes {
        name
      }
    }
  `)

export class CustomerAttributeOp {
  static getFromGroups = <
    TCustomerAttributeGroup extends ResultOf<
      typeof customerAttributeOpGetFromGroupsCustomerAttributeGroupFragment
    >,
  >(
    customerAttributeGroups: TCustomerAttributeGroup[],
    customerAttributeId: string,
  ) => {
    const customerAttributeGroup = customerAttributeGroups.find(
      (customerAttributeGroup) =>
        customerAttributeGroup.customerAttributes?.some(
          (customerAttribute) => customerAttribute.id === customerAttributeId,
        ),
    )

    const customerAttribute = customerAttributeGroup?.customerAttributes?.find(
      (customerAttribute) => customerAttribute.id === customerAttributeId,
    ) as
      | NonNullable<TCustomerAttributeGroup['customerAttributes']>[number]
      | undefined

    return { customerAttribute, customerAttributeGroup }
  }

  // eslint-disable-next-line jsdoc/require-param, jsdoc/require-returns
  /**
   * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
   * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
   */
  static getNameFromGroups = <
    TCustomerAttributeGroup extends ResultOf<
      typeof customerAttributeOpGetNameFromGroupsCustomerAttributeGroupFragment
    >,
  >(
    customerAttributeGroups: TCustomerAttributeGroup[],
    customerAttributeId: string,
    t: TFunction,
  ) => {
    const { customerAttribute, customerAttributeGroup } = this.getFromGroups(
      customerAttributeGroups,
      customerAttributeId,
    )

    return {
      customerAttributeName:
        customerAttribute?.name ?? t('unknown_customized_segments_1'),
      customerAttributeGroupName:
        customerAttributeGroup?.name ??
        t('unknown_customized_segments_group_2'),
    }
  }
}

const surveyGroupAnswerOptionOpToTextSurveyGroupAnswerOptionFragment = graphql(`
  fragment SurveyGroupAnswerOptionOp_ToText_SurveyGroupAnswerOption on SurveyGroupAnswerOption {
    option
    text
  }
`)

export class SurveyGroupAnswerOptionOp {
  static toText = ({
    option,
    text,
  }: ResultOf<
    typeof surveyGroupAnswerOptionOpToTextSurveyGroupAnswerOptionFragment
  >) => compact([Number.isFinite(Number(option)) && option, text]).join(' - ')
}
